// AccordionGroup — the mobile render of ProjectDetail's RailGroup (mobile
// overhaul Phase 0 §0.5.5; mockup §6.1 B, class .agrp). A 48px header button:
// mono title, optional badge, and — while closed — a one-line summary so the
// collapsed group still says what's inside. Open/closed persists per slug
// under the SAME localStorage key RailGroup uses, so a group collapsed on the
// desktop rail is collapsed on the phone too.

import { useState, type ReactNode } from 'react';

export function AccordionGroup({
  slug,
  title,
  badge,
  status,
  summary,
  defaultOpen = true,
  children,
}: {
  /** Shared with RailGroup — the localStorage key is derived from it. */
  slug: string;
  title: string;
  /** Small trailing note on the title ("ex GST", "2 people"). */
  badge?: string;
  status?: ReactNode;
  /** One line shown only while closed; ellipsised, right-aligned. */
  summary?: ReactNode;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const storageKey = `ss-os-rail-group-${slug}`;
  const [expanded, setExpanded] = useState(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      return stored === null ? defaultOpen : stored !== 'false';
    } catch {
      return defaultOpen; // storage blocked — groups just take their default
    }
  });

  const toggle = () => {
    const next = !expanded;
    setExpanded(next);
    try {
      localStorage.setItem(storageKey, String(next));
    } catch {
      // collapse still works for this page-load; it just won't persist
    }
  };

  return (
    <section className="agrp-group" data-rail-group={slug}>
      <button
        type="button"
        className={`agrp${expanded ? ' open' : ''}`}
        aria-expanded={expanded}
        onClick={toggle}
      >
        <span className="k">
          {title}
          {badge && <small>{badge}</small>}
        </span>
        {status}
        {!expanded && summary !== undefined && <span className="sum">{summary}</span>}
        <span className="chev" aria-hidden="true">
          ›
        </span>
      </button>
      {expanded && <div className="agrp-body">{children}</div>}
    </section>
  );
}
